import { Suspense, useState } from "react";
import { FormProvider } from "react-hook-form";
import type { LocationGenerics } from "@/router/locationGenerics";

import {
  ConnectionWithCredentialsQuery,
  useConnectionWithCredentialsQuery,
  useIntegrationConfigurationSuspenseQuery,
} from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Centered from "@/components/elements/layout/Centered";
import { useUpdateConnection } from "@/integrations";
import { useToast } from "@/providers/ToastProvider";
import { useMatch, useNavigate } from "@tanstack/react-location";

import ConnectionForm, {
  AuthorizeMethod,
  ConnectionFormSubmitButton,
  useConnectionForm,
} from "./ConnectionForm/ConnectionForm";

export function EditConnectionPage({
  authorizeMethods,
}: {
  authorizeMethods?: AuthorizeMethod[];
}) {
  const {
    params: { connectionId },
  } = useMatch<LocationGenerics>();

  const { data } = useConnectionWithCredentialsQuery({
    variables: {
      id: connectionId,
    },
  });
  if (data?.connection == null) {
    return <LoadingSpinner />;
  }
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <EditConnectionPageContent
        connection={data.connection}
        authorizeMethods={authorizeMethods}
      />
    </Suspense>
  );
}

function EditConnectionPageContent({
  connection,
  authorizeMethods,
}: {
  connection: ConnectionWithCredentialsQuery["connection"];
  authorizeMethods?: AuthorizeMethod[];
}) {
  const { data } = useIntegrationConfigurationSuspenseQuery({
    variables: {
      id: connection.integrationId,
    },
  });
  const integration = data.integrationConfiguration;

  const navigate = useNavigate();
  const toast = useToast();

  const [selectedMethod, onMethodChanged] = useState<AuthorizeMethod>(
    authorizeMethods?.[0] ?? AuthorizeMethod.Self,
  );

  const formMethods = useConnectionForm(integration, connection.label, {
    defaultValues: {
      ...connection.credentials,
    },
  });

  const { loading: updateConnectionLoading, updateConnection } =
    useUpdateConnection(connection.id, integration, {
      onSuccess() {
        toast(
          "Connection updated",
          "The connection has been successfully updated",
          "success",
        );
        navigate({ to: ".." });
      },
      onError(e) {
        toast("Connection not updated", e.message, "error");
      },
    });

  if (!integration.requiresConfiguration) {
    navigate({ to: ".." });
  }

  return (
    <Centered>
      <FormProvider {...formMethods}>
        <form
          id="integration-form"
          onSubmit={formMethods.handleSubmit((data) => {
            return updateConnection(data);
          })}
        >
          <div className="w-full max-w-lg">
            <ConnectionForm
              integration={integration}
              connectionId={connection.id}
              isLoading={updateConnectionLoading}
              authorizeMethods={authorizeMethods}
              onAuthorizeMethodChanged={onMethodChanged}
              currentConnectionFormId={connection.formId}
            />
            {selectedMethod === AuthorizeMethod.Self && (
              <div className="mt-8">
                <ConnectionFormSubmitButton
                  integration={integration}
                  isLoading={updateConnectionLoading}
                  isEditMode
                />
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </Centered>
  );
}
